import React, { useEffect } from "react";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import Button from "@components/buttons/Button/Button";
import Card from "@components/cards/Card/Card";
import Header2 from "@components/text/Header2/Header2";
import { useAuth } from "@context/auth-context";
import { capitaliseFirstLetter } from "@util/string";
import { useNavigate } from "react-router-dom";
import { UserType } from "@models/User";
import { getCustomerPortal } from "@services/paymentService";
import commonStyles from "@styles/CommonPageLayout.module.css";
import styles from "./AccountPage.module.css";

const SHOW_BUSINESS_INFO_USER_TYPES: UserType[] = ["business_admin", "business_user"];
const SHOW_PURCHASE_CONFIGURE_USER_TYPES: UserType[] = ["business_admin"];

const AccountPage: React.FC = () => {
    const navigate = useNavigate();
    const { authState, isSubscribed, logout, refreshUser } = useAuth();
    const { user } = authState;

    useEffect(() => {
        void refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogOut = async () => {
        try {
            await logout();
            navigate("/");
        } catch {
            alert("Error logging out. Please try again.");
        }
    };

    const openCustomerPortal = async () => {
        try {
            const portalUrl = await getCustomerPortal();
            window.location.href = portalUrl;
        } catch {
            alert("Error moving to customer portal. Please try again.");
        }
    };

    if (!user) {
        return <div>User not logged in</div>;
    }

    const { firstName, lastName, businessName } = user;
    const isBusinessUser = SHOW_BUSINESS_INFO_USER_TYPES.includes(user.userType);
    const creditsRemaining = isBusinessUser ? (user.memorialCreditAllocation ?? 0) : user.memorialCredits;
    const bonusCredits = user.bonusMemorialCredits;
    const canManageSubscription = SHOW_PURCHASE_CONFIGURE_USER_TYPES.includes(user.userType);

    return (
        <div className={commonStyles.container}>
            <h1>My Account</h1>
            <Card className={styles.container}>
                <div className={styles.userInfo}>
                    <Header2>{user.userType === "business_admin" ? "Business Account" : `${firstName} ${lastName}`}</Header2>
                    {user && SHOW_BUSINESS_INFO_USER_TYPES.includes(user.userType) && (
                        <div className={styles.businessName}>
                            {businessName}
                        </div>
                    )}
                </div>
                <div className={styles.statGrid}>
                    <div className={styles.statCard}>
                        <ConfirmationNumberOutlinedIcon className={styles.statIcon} />
                        <div className={styles.statText}>
                            <div className={styles.statValue}>{creditsRemaining}</div>
                            <div className={styles.statLabel}>Memorial Credits</div>
                        </div>
                    </div>
                    {bonusCredits > 0 && (
                        <div className={styles.statCard}>
                            <StarOutlineIcon className={styles.statIcon} />
                            <div className={styles.statText}>
                                <div className={styles.statValue}>{bonusCredits}</div>
                                <div className={styles.statLabel}>Bonus Credits</div>
                            </div>
                        </div>
                    )}
                    {canManageSubscription && (
                        <div className={styles.statCard}>
                            <CardMembershipIcon className={styles.statIcon} />
                            <div className={styles.statText}>
                                <div className={styles.statValue}>{capitaliseFirstLetter(user.subscription || "none")}</div>
                                <div className={styles.statLabel}>Subscription</div>
                            </div>
                        </div>
                    )}
                </div>
                {canManageSubscription && isSubscribed && (
                    <p className={styles.note}>
                        Monthly allocation resets on the 1st of each month.
                    </p>
                )}
                {user?.paymentLocked && (
                    <div className={styles.paymentLockedBanner}>
                        <p>Your payment method needs attention. Please update it to continue creating memorials.</p>
                        <Button
                            variant="submit"
                            size="medium"
                            onClick={openCustomerPortal}
                        >
                            Update Payment Method
                        </Button>
                    </div>
                )}
                <div className={styles.buttons}>
                    {!isSubscribed && canManageSubscription && (
                        <Button
                            variant="submit"
                            size="medium"
                            to="/account/subscribe"
                        >
                            Activate Platform Access
                        </Button>
                    )}
                    {(user && !["root_admin", "business_user"].includes(user.userType)) && (
                        <div className={styles.invoicesBtnContainer}>
                            <Button
                                variant="text"
                                size="medium"
                                onClick={openCustomerPortal}
                            >
                                {`${isSubscribed ? "Manage Subscription & " : ""}Invoices`}
                                {" "}
                                &gt;
                            </Button>
                        </div>
                    )}

                </div>
                <Button
                    variant="secondary"
                    size="medium"
                    onClick={handleLogOut}
                >
                    Sign Out
                </Button>
            </Card>
        </div>
    );
};

export default AccountPage;
